/*
 * assets/dfo-scheduler.js
 * FINAL VERSION: Smart Alternating Logic (Day 1 vs Day 2 check)
 */

const TOTAL_SLOTS = 8; // 8 Delivery days

function autoAllocateSchedule(items) {
    
    // --- 1. SETUP ---
    const deliverySchedule = {}; 
    const slots = [];
    
    // Initialize Days
    for (let i = 1; i <= TOTAL_SLOTS; i++) {
        const dayKey = `Day ${i}`;
        deliverySchedule[dayKey] = [];
        slots.push({ id: i, dayKey: dayKey, weekIndex: Math.ceil(i / 2), index: i - 1 }); 
    }

    const addToDay = (dayIndex, itemUnit) => {
        if (dayIndex >= TOTAL_SLOTS) return; 
        const dayKey = slots[dayIndex].dayKey;
        deliverySchedule[dayKey].push(itemUnit);
    };

    // Helper to get total weight of a specific day index (0-7)
    const getDayWeight = (dayIndex) => {
        if (dayIndex >= TOTAL_SLOTS) return 0;
        const dayKey = slots[dayIndex].dayKey;
        return deliverySchedule[dayKey].reduce((sum, item) => sum + item.weight_g, 0);
    };

    // --- 2. PREPARE ITEMS ---
    const recurringItems = []; 
    const singleItems = [];    

    items.forEach(item => {
        const units = [];
        for(let i=0; i<item.frequency; i++) {
            units.push({
                name: item.name,
                weight_g: item.unit_weight_g,
                frequency: item.frequency
            });
        }
        if (item.frequency > 1) {
            recurringItems.push({ ...item, units: units });
        } else {
            singleItems.push(...units); 
        }
    });

    // Sort heavy items first so they establish the "base" of the schedule
    recurringItems.sort((a, b) => b.item_total_g - a.item_total_g);

    // --- 3. PHASE 1: RECURRING ITEMS (Smart Placement) ---
    
    recurringItems.forEach(item => {
        const freq = item.frequency;
        const units = item.units;

        if (freq === 8) {
            // Freq 8: Every Day
            for (let i = 0; i < 8; i++) addToDay(i, units[i]);
        } 
        else if (freq === 4) {
            // --- LOGIC FOR ALTERNATE DAYS (Your Request) ---
            // We check: Is "Day 1 series" heavier? Or "Day 2 series"?
            
            // Calculate weight of Odd Days (Indices 0, 2, 4, 6)
            let weightOddDays = getDayWeight(0) + getDayWeight(2) + getDayWeight(4) + getDayWeight(6);
            
            // Calculate weight of Even Days (Indices 1, 3, 5, 7)
            let weightEvenDays = getDayWeight(1) + getDayWeight(3) + getDayWeight(5) + getDayWeight(7);

            let startOffset = 0; // Default start: Day 1
            
            // If Odd Days are already heavier, switch this item to start on Day 2
            if (weightOddDays > weightEvenDays) {
                startOffset = 1; // Start: Day 2
            }

            // Distribute 4 units starting from the chosen offset
            for (let i = 0; i < 4; i++) {
                addToDay((i * 2) + startOffset, units[i]);
            }
        } 
        else if (freq === 2) {
            // --- LOGIC FOR FREQ 2 ---
            // Check all 4 possible pairs to find the lightest spots
            // Pair A: Day 1 & 5 | Pair B: Day 2 & 6 | Pair C: Day 3 & 7 | Pair D: Day 4 & 8
            
            let bestOffset = 0;
            let minPairWeight = Infinity;

            for (let offset = 0; offset < 4; offset++) {
                // Check weight of current day + the day 4 slots ahead
                let currentPairWeight = getDayWeight(offset) + getDayWeight(offset + 4);
                
                if (currentPairWeight < minPairWeight) {
                    minPairWeight = currentPairWeight;
                    bestOffset = offset;
                }
            }

            addToDay(bestOffset, units[0]);
            addToDay(bestOffset + 4, units[1]);
        }
    });

    // --- 4. PHASE 2: SINGLE ITEMS (Load Balancing) ---
    singleItems.sort((a, b) => b.weight_g - a.weight_g);

    singleItems.forEach(unit => {
        // Find the absolute lightest day among all 8 days
        let lightestDayIndex = 0;
        let minWeight = Infinity;

        for (let i = 0; i < TOTAL_SLOTS; i++) {
            const currentWeight = getDayWeight(i);
            if (currentWeight < minWeight) {
                minWeight = currentWeight;
                lightestDayIndex = i;
            }
        }
        addToDay(lightestDayIndex, unit);
    });

    // --- 5. FINISH ---
    let totalWeightG = 0;
    Object.values(deliverySchedule).forEach(dayItems => {
        dayItems.forEach(it => totalWeightG += it.weight_g);
    });

    return { 
        slots, 
        schedule_detail: deliverySchedule,
        total_weight_g: totalWeightG
    };
}

// --- VIEW RENDERER ---
function renderScheduleListView(scheduleResult) {
    const { slots, schedule_detail } = scheduleResult;
    let html = '<div class="dfo-schedule-accordion">';
    let currentWeek = 0;
    let dayCounter = 1;

    for (const slot of slots) {
        const dayKey = slot.dayKey;
        const week = slot.weekIndex;
        const items = schedule_detail[dayKey];
        const dayWeightG = items.reduce((sum, it) => sum + it.weight_g, 0);
        const dayWeightKg = (dayWeightG / 1000).toFixed(3).replace(/(\.000|00)$/, ''); 

        if (week > currentWeek) {
            html += `<h4 style="margin:16px 0 8px;">📅 Week ${week}</h4>`;
            currentWeek = week;
        }

        const grouped = items.reduce((acc, item) => {
            const key = `${item.name}-${item.weight_g}`;
            if (!acc[key]) acc[key] = { name: item.name, count: 0, weight_g: item.weight_g };
            acc[key].count++;
            return acc;
        }, {});
        
        const uniqueItemCount = Object.keys(grouped).length;
        const summaryText = `${uniqueItemCount} unique item${uniqueItemCount !== 1 ? 's' : ''}`;
        
        const isDefaultOpen = dayCounter === 1; 
        const contentId = `dfo-day-content-${dayCounter}`;
        
        let contentHtml = `<ul class="dfo-day-item-list">`;
        Object.values(grouped).map(group => {
            const totalKgForThisItemToday = (group.count * group.weight_g / 1000).toFixed(3).replace(/(\.000|00)$/, '');
            contentHtml += `<li style="padding: 4px 0;">${group.name}: <strong>${totalKgForThisItemToday} KG</strong></li>`;
        });
        contentHtml += `</ul>`;

        html += `<div class="dfo-accordion-item">
            <button class="dfo-accordion-header" 
                    data-target="#${contentId}" 
                    aria-expanded="${isDefaultOpen}" 
                    aria-controls="${contentId}">
                <strong class="dfo-day-title">${dayKey} (${dayWeightKg} KG)</strong>
                <span class="dfo-day-summary">${summaryText}</span>
                <span class="dfo-accordion-icon">${isDefaultOpen ? '&#9660;' : '&#9658;'}</span>
            </button>
            <div id="${contentId}" class="dfo-accordion-content" style="display: ${isDefaultOpen ? 'block' : 'none'}; max-height: ${isDefaultOpen ? '500px' : '0'};">
                ${contentHtml}
            </div>
        </div>`;
        dayCounter++;
    }
    html += '</div>'; 
    return html;
}

window.DFO_SCHED = {
    generateAndRenderSchedule: function(items) {
        const scheduleResult = autoAllocateSchedule(items);
        const scheduleHtml = renderScheduleListView(scheduleResult);
        return {
            payload: {
                items: items,
                total_weight_g: scheduleResult.total_weight_g,
                schedule_detail: scheduleResult.schedule_detail,
                schedule_html: scheduleHtml, 
                schedule_json: scheduleResult.schedule_detail,
                generated_at: new Date().toISOString()
            },
            html: scheduleHtml
        };
    }
};